import { z } from 'zod';
import { BusinessDateSchema, IdSchema } from './encoding.js';
/**
 * Client / CRM contracts (ERD §7.5; FRD-CRM-006 to CRM-032). Only the Release 1 scope: the client
 * master with its addresses, contacts and activities (FRD-CRM-001). Hierarchy, communication
 * preferences, payment terms, credit limits, opportunities, quotations and tickets are Phase 3.
 *
 * The Client ID (`code`) is generated by the system from its configured series (FRD-CRM-008): it
 * is in every response and in no request. Industry, client type, source and activity type carry
 * the value code of a configurable list (SDD §15.3); the values are business data.
 */
const MasterStatusSchema = z.enum(['active', 'inactive']);
const NameSchema = z.string().trim().min(1).max(200);
const EmailSchema = z.email().max(254);
const PhoneSchema = z.string().trim().min(1).max(50);
const ListCodeValueSchema = z.string().trim().min(1).max(100);
const TextSchema = z.string().trim().min(1).max(4000);
/**
 * The client lifecycle stages of BRD §11.5 (FRD-CRM-013). `opportunity` belongs to opportunity
 * management (Phase 3): the API refuses it in Release 1. No transition rule between stages is
 * defined by the sources; every change is audited.
 */
export const CLIENT_LIFECYCLE_STAGES = [
    'lead',
    'prospect',
    'qualified',
    'opportunity',
    'client',
    'active_account',
    'renewal_expansion',
    'inactive',
];
export const ClientLifecycleStageSchema = z.enum(CLIENT_LIFECYCLE_STAGES);
export const ClientSchema = z.object({
    id: IdSchema,
    /** The generated Client ID. */
    code: z.string(),
    companyName: z.string(),
    industryCode: z.string().nullable(),
    website: z.string().nullable(),
    email: z.string().nullable(),
    phone: z.string().nullable(),
    clientTypeCode: z.string().nullable(),
    sourceCode: z.string().nullable(),
    /** The employee responsible for the relationship (FRD-CRM-011). */
    accountOwnerEmployeeId: IdSchema,
    lifecycleStage: ClientLifecycleStageSchema,
    notes: z.string().nullable(),
    /** Sensitive (category `trn`): masked unless the caller holds `crm.client:view_unmasked`. */
    trn: z.string().nullable(),
    status: MasterStatusSchema,
    /** For `If-Match` on a change (SDD §7.3). */
    version: z.number().int(),
});
export const CreateClientRequestSchema = z.strictObject({
    companyName: NameSchema,
    industryCode: ListCodeValueSchema.optional(),
    website: z.string().trim().min(1).max(500).optional(),
    email: EmailSchema.optional(),
    phone: PhoneSchema.optional(),
    clientTypeCode: ListCodeValueSchema.optional(),
    sourceCode: ListCodeValueSchema.optional(),
    accountOwnerEmployeeId: IdSchema,
    lifecycleStage: ClientLifecycleStageSchema,
    notes: TextSchema.optional(),
    trn: z.string().trim().min(1).max(50).optional(),
});
/** A partial change. An optional field is cleared with `null`; the Client ID never changes. */
export const UpdateClientRequestSchema = z.strictObject({
    companyName: NameSchema.optional(),
    industryCode: ListCodeValueSchema.nullable().optional(),
    website: z.string().trim().min(1).max(500).nullable().optional(),
    email: EmailSchema.nullable().optional(),
    phone: PhoneSchema.nullable().optional(),
    clientTypeCode: ListCodeValueSchema.nullable().optional(),
    sourceCode: ListCodeValueSchema.nullable().optional(),
    accountOwnerEmployeeId: IdSchema.optional(),
    lifecycleStage: ClientLifecycleStageSchema.optional(),
    notes: TextSchema.nullable().optional(),
    trn: z.string().trim().min(1).max(50).nullable().optional(),
    status: MasterStatusSchema.optional(),
});
/** The client's addresses, including the invoicing one (FRD-CRM-010; ERD §7.5). */
export const CLIENT_ADDRESS_TYPES = ['registered', 'invoicing', 'other'];
export const ClientAddressTypeSchema = z.enum(CLIENT_ADDRESS_TYPES);
export const ClientAddressSchema = z.object({
    id: IdSchema,
    clientId: IdSchema,
    addressType: ClientAddressTypeSchema,
    addressLine1: z.string(),
    addressLine2: z.string().nullable(),
    city: z.string(),
    country: z.string(),
    version: z.number().int(),
});
export const CreateClientAddressRequestSchema = z.strictObject({
    addressType: ClientAddressTypeSchema,
    addressLine1: z.string().trim().min(1).max(200),
    addressLine2: z.string().trim().min(1).max(200).optional(),
    city: z.string().trim().min(1).max(100),
    country: z.string().trim().min(1).max(100),
});
export const UpdateClientAddressRequestSchema = z.strictObject({
    addressType: ClientAddressTypeSchema.optional(),
    addressLine1: z.string().trim().min(1).max(200).optional(),
    addressLine2: z.string().trim().min(1).max(200).nullable().optional(),
    city: z.string().trim().min(1).max(100).optional(),
    country: z.string().trim().min(1).max(100).optional(),
});
/** Contact persons (FRD-CRM-019 to CRM-024). At most one primary contact per client. */
export const ClientContactSchema = z.object({
    id: IdSchema,
    clientId: IdSchema,
    name: z.string(),
    designation: z.string().nullable(),
    email: z.string().nullable(),
    phone: z.string().nullable(),
    isPrimary: z.boolean(),
    status: MasterStatusSchema,
    version: z.number().int(),
});
export const CreateClientContactRequestSchema = z.strictObject({
    name: NameSchema,
    designation: NameSchema.optional(),
    email: EmailSchema.optional(),
    phone: PhoneSchema.optional(),
    /** Makes the new contact the primary one, replacing the current primary in the same change. */
    isPrimary: z.boolean().optional(),
});
/** The primary flag changes only through the set-primary action, so the swap stays atomic. */
export const UpdateClientContactRequestSchema = z.strictObject({
    name: NameSchema.optional(),
    designation: NameSchema.nullable().optional(),
    email: EmailSchema.nullable().optional(),
    phone: PhoneSchema.nullable().optional(),
    status: MasterStatusSchema.optional(),
});
/**
 * Client activities (FRD-CRM-025 to CRM-032). `status` is the minimal follow-up state: the sources
 * name "a status" (FRD-CRM-027) without values, so an activity is open until it is completed.
 */
export const ClientActivityStatusSchema = z.enum(['open', 'completed']);
export const ClientActivitySchema = z.object({
    id: IdSchema,
    clientId: IdSchema,
    /** The client's ID and name, so a follow-up list across clients names each one. */
    clientCode: z.string(),
    clientName: z.string(),
    contactId: IdSchema.nullable(),
    activityTypeCode: z.string(),
    activityDate: z.string(),
    responsibleEmployeeId: IdSchema,
    subject: z.string().nullable(),
    description: z.string().nullable(),
    nextAction: z.string().nullable(),
    dueDate: z.string().nullable(),
    status: ClientActivityStatusSchema,
    projectId: IdSchema.nullable(),
    /** The project's code and name, as the projects module labels it; null when none is named. */
    projectLabel: z.string().nullable(),
    version: z.number().int(),
});
const SubjectSchema = z.string().trim().min(1).max(200);
export const CreateClientActivityRequestSchema = z
    .strictObject({
    activityTypeCode: ListCodeValueSchema,
    activityDate: BusinessDateSchema,
    contactId: IdSchema.optional(),
    responsibleEmployeeId: IdSchema,
    subject: SubjectSchema.optional(),
    description: TextSchema.optional(),
    nextAction: z.string().trim().min(1).max(1000).optional(),
    dueDate: BusinessDateSchema.optional(),
    projectId: IdSchema.optional(),
})
    .refine((value) => value.subject !== undefined || value.description !== undefined, 'an activity records a subject or a description (FRD-CRM-026)');
/** A partial change; the client never changes, and completion is its own action. */
export const UpdateClientActivityRequestSchema = z.strictObject({
    activityTypeCode: ListCodeValueSchema.optional(),
    activityDate: BusinessDateSchema.optional(),
    contactId: IdSchema.nullable().optional(),
    responsibleEmployeeId: IdSchema.optional(),
    subject: SubjectSchema.nullable().optional(),
    description: TextSchema.nullable().optional(),
    nextAction: z.string().trim().min(1).max(1000).nullable().optional(),
    dueDate: BusinessDateSchema.nullable().optional(),
    projectId: IdSchema.nullable().optional(),
});
/** An action that carries nothing but its `If-Match` (set primary, complete). */
export const EmptyActionRequestSchema = z.strictObject({});
/**
 * One value of a configurable CRM list (SDD §15.3). Inactive values are included so existing
 * records keep their label; only active ones may be chosen for new work.
 */
export const CrmListValueSchema = z.object({
    valueCode: z.string(),
    label: z.string(),
    active: z.boolean(),
});
/** The values of the CRM lists, for the pickers and for naming stored codes. */
export const CrmListsSchema = z.object({
    industry: z.array(CrmListValueSchema),
    clientType: z.array(CrmListValueSchema),
    clientSource: z.array(CrmListValueSchema),
    activityType: z.array(CrmListValueSchema),
});
//# sourceMappingURL=crm.js.map